/**
 * VÉRAULT Portal — "Still needed from you" (client-facing).
 *
 * Calm list of the confirmation items still awaiting the client:
 * unanswered questions and unapproved flight journeys. Revision
 * requests show as submitted · with your advisor. Silent when
 * nothing is outstanding. Updates live on approval/autosave events.
 */
import React from "react";
import { Circle, MessageCircle, ArrowDown } from "lucide-react";
import { Surface } from "../index";
import { useConfirmationsSummary } from "./ConfirmationsProgress";

function jumpTo(it) {
  const sel = it.kind === "flight"
    ? `[data-testid="flight-journey-card-${it.id}"]`
    : `[data-testid="smart-confirmation-card-${it.id}"]`;
  const el = document.querySelector(sel);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-[hsl(var(--accent-gold))]", "ring-offset-4");
  setTimeout(() => el.classList.remove("ring-2", "ring-[hsl(var(--accent-gold))]", "ring-offset-4"), 2000);
}

function pendingCopy(it) {
  if (it.kind === "flight") {
    const name = it.label.replace(/^Flights\s*·\s*/, "");
    return `Approve or request a revision for the ${name} flights`;
  }
  return `Answer: ${it.label}`;
}

export default function OutstandingActionsCard({ endpoint, testIdBase = "outstanding-actions" }) {
  const data = useConfirmationsSummary(endpoint);
  if (!data || !data.total) return null;
  const pending = data.items.filter((i) => i.status === "pending");
  const revisions = data.items.filter((i) => i.status === "revision_requested");
  if (!pending.length && !revisions.length) return null;

  return (
    <Surface variant="paper" padded className="!p-5" data-testid={testIdBase}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--accent-gold))]">
          Still needed from you
        </p>
        <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted))]" data-testid={`${testIdBase}-count`}>
          {pending.length} remaining
        </span>
      </div>
      <ul className="mt-3 space-y-2" data-testid={`${testIdBase}-list`}>
        {pending.map((it) => (
          <li key={`${it.kind}-${it.id}`} className="flex items-center gap-2.5" data-testid={`${testIdBase}-item-${it.id}`}>
            <Circle className="size-3 shrink-0 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
            <span className="font-serif text-[14px] text-[hsl(var(--foreground))] min-w-0 truncate">
              {pendingCopy(it)}
            </span>
            <button
              type="button"
              onClick={() => jumpTo(it)}
              className="ml-auto shrink-0 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] hover:text-[hsl(var(--foreground))]"
              data-testid={`${testIdBase}-item-${it.id}-jump`}
            >
              <ArrowDown className="size-3" strokeWidth={1.5} />
              Review
            </button>
          </li>
        ))}
        {revisions.map((it) => (
          <li key={`${it.kind}-${it.id}`} className="flex items-center gap-2.5" data-testid={`${testIdBase}-revision-${it.id}`}>
            <MessageCircle className="size-3 shrink-0 text-[hsl(var(--accent-terracotta))]" strokeWidth={1.5} />
            <span className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] min-w-0 truncate">
              {it.label.replace(/^Flights\s*·\s*/, "")} — revision requested · with your advisor
            </span>
          </li>
        ))}
      </ul>
    </Surface>
  );
}
